import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaEnvelope, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #222, #444);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  padding: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.div`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const WelcomeText = styled.h1`
  font-size: 36px;
  margin-bottom: 20px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 16px;
  max-width: 400px;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.8;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  padding: 40px;
`;

const ResetForm = styled.form`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 10px;
  padding: 40px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 10px;
  color: #333;
  text-align: center;
`;

const FormSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 25px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 15px 15px 50px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#eee'};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;
  
  &:focus {
    border-color: #333;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    outline: none;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #222;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    background-color: #000;
  }
  
  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
  
  svg {
    margin-left: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #27ae60;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #e8f8f0;
  border-radius: 6px;
  text-align: center;
`;

const BackToLogin = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
  font-size: 14px;
  
  a {
    display: flex;
    align-items: center;
    color: #555;
    text-decoration: none;
    
    svg {
      margin-right: 8px;
    }
    
    &:hover {
      color: #222;
      text-decoration: underline;
    }
  }
`;

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { resetPassword, error, clearError, isAuthenticated } = useAuth();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  // Update form error if auth context has an error
  useEffect(() => {
    if (error) {
      setFormError(error);
    }
  }, [error]);
  
  const handleReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setFormError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setFormError('');
    setSuccessMessage('');
    clearError();
    
    try {
      await resetPassword(email);
      setSuccessMessage(`Password reset instructions sent to ${email}. Please check your inbox.`);
      
      // Clear form
      setEmail('');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 5000);
      
    } catch (err) {
      console.error('Reset password error:', err);
      
      // Display specific form errors
      switch (err.code) {
        case 'auth/user-not-found':
          setFormError('No account exists with this email address');
          break;
        case 'auth/invalid-email':
          setFormError('Invalid email format');
          break;
        default:
          if (!formError) {
            setFormError('Failed to send reset email. Please try again');
          }
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <LeftPanel>
        <Logo>Godai Studios</Logo>
        <WelcomeText>Admin Portal</WelcomeText>
        <Description>
          Reset your password to regain access to the administration dashboard.
        </Description>
      </LeftPanel>
      <RightPanel>
        <ResetForm onSubmit={handleReset}>
          <FormTitle>Reset Password</FormTitle>
          <FormSubtitle>
            Enter your email address and we'll send you instructions to reset your password
          </FormSubtitle>
          
          {formError && <ErrorMessage>{formError}</ErrorMessage>}
          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          
          <InputGroup>
            <InputIcon>
              <FaEnvelope />
            </InputIcon>
            <Input 
              type="email" 
              placeholder="Email Address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={formError.includes('email')}
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Reset Password'} {!loading && <FaArrowRight />}
          </Button>
          
          <BackToLogin>
            <Link to="/login">
              <FaArrowLeft /> Back to Sign In
            </Link>
          </BackToLogin>
        </ResetForm>
      </RightPanel>
    </Container>
  );
};

export default ResetPassword; 